// Second leg of the Gini Code → Gini Studio handoff. Gini Code already has
// a signed-in session; it calls simo-auth's /api/oauth/vscode-code with its
// own bearer token and opens this page with the one-time code+state that
// call returned. We exchange it here for the same session (daemon sets the
// httpOnly cookie), so the user never sees a login form when arriving this
// way. See simocode-core/src/simo-auth/oauth.ts for the code's 5-minute TTL
// and the callback allowlist this URL had to be added to.

import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n';
import { exchangeOAuthCode } from '../providers/auth';
import { Icon } from './Icon';
import styles from './Welcome.module.css';

interface Props {
  onSignedIn: () => void;
  onFallbackToLogin: () => void;
}

export function AuthCallback({ onSignedIn, onFallbackToLogin }: Props) {
  const t = useT();
  const [failed, setFailed] = useState(false);
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    // Strip code/state from the URL immediately, before the exchange even
    // starts — not just on success. The authorization code and state are
    // one-time-use; leaving them in the URL bar risks re-submission on
    // refresh/back-navigation and exposure via browser history or a
    // referrer header on any outbound request from this page. This does
    // not affect the exchange itself, which already read both values.
    window.history.replaceState(null, '', window.location.pathname);
    if (!code || !state) {
      setFailed(true);
      return;
    }
    void exchangeOAuthCode(code, state).then((result) => {
      if (result.ok) {
        onSignedIn();
      } else {
        setFailed(true);
      }
    });
  }, [onSignedIn]);

  return (
    <div className={styles.shell}>
      <div className={styles.card}>
        {failed ? (
          <>
            <h1 className={styles.title}>{t('authCallback.failed')}</h1>
            <p className={styles.subtitle}>{t('authCallback.retryHint')}</p>
            <button type="button" className={styles.submit} onClick={onFallbackToLogin}>
              {t('welcome.loginButton')}
            </button>
          </>
        ) : (
          <>
            <h1 className={styles.title}>
              <Icon name="spinner" size={16} className={styles.spinner} /> {t('authCallback.connecting')}
            </h1>
          </>
        )}
      </div>
    </div>
  );
}
